import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconFilter, IconRefresh, IconX } from '@tabler/icons-react';
import type { BoardStage } from '../../../../entities/board';
import type { Group } from '../../../../entities/group';
import { fetchSourceOptions } from '../../../../entities/lead';
import type { User } from '../../../../entities/user';
import { pb } from '../../../../shared/api/pocketbase';
import { Button } from '../../../../shared/ui/Button';
import { DateInput } from '../../../../shared/ui/DateInput';
import { Select } from '../../../../shared/ui/Select';

const API_BASE = import.meta.env.DEV ? 'http://127.0.0.1:8090' : '';

interface DealTimeRow {
  id: string;
  name: string;
  avgDays: number;
  count: number;
  rank: number;
}

interface DealTimeResponse {
  total: { avgDays: number; count: number };
  managers: DealTimeRow[];
  groups: DealTimeRow[];
}

const formatAvgDays = (d: number): string => {
  if (d < 1) return `${Math.round(d * 24)} ч.`;
  const days = Math.floor(d);
  const hours = Math.round((d - days) * 24);
  return hours > 0 ? `${days} дн. ${hours} ч.` : `${days} дн.`;
};

const RankTable: React.FC<{ title: string; rows: DealTimeRow[] }> = ({ title, rows }) => (
  <div className="af-daily">
    <div className="af-daily-title">{title}</div>
    <div className="af-daily-wrap">
      <table className="af-daily-table">
        <thead>
          <tr>
            <th className="af-th dt-th-rank">#</th>
            <th className="af-th af-th-date">Имя</th>
            <th className="af-th">Сделок</th>
            <th className="af-th">Среднее время</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.id} className="af-tr">
              <td className="af-td dt-td-rank">{row.rank}</td>
              <td className="af-td af-td-date">{row.name}</td>
              <td className="af-td">{row.count}</td>
              <td className="af-td">{formatAvgDays(row.avgDays)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const DealTimeSection: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [stages, setStages] = useState<BoardStage[]>([]);

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [realizationStart, setRealizationStart] = useState('');
  const [realizationEnd, setRealizationEnd] = useState('');
  const [creationStart, setCreationStart] = useState('');
  const [creationEnd, setCreationEnd] = useState('');
  const [managerIds, setManagerIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [boardIds, setBoardIds] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);

  const [data, setData] = useState<DealTimeResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    pb.collection('users')
      .getFullList<User>({ sort: 'name', fields: 'id,name,email,role,created' })
      .then(setUsers)
      .catch(() => setUsers([]));
    pb.collection('groups')
      .getFullList<Group>({ sort: '-created', fields: 'id,name,managers,usersIds,created,updated' })
      .then(setGroups)
      .catch(() => setGroups([]));
    pb.collection('board')
      .getFullList<BoardStage>({ sort: 'order', filter: 'actual=true', fields: 'id,name,order,actual' })
      .then(setStages)
      .catch(() => setStages([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (realizationStart) params.set('realizationStart', realizationStart);
      if (realizationEnd) params.set('realizationEnd', realizationEnd);
      if (creationStart) params.set('creationStart', creationStart);
      if (creationEnd) params.set('creationEnd', creationEnd);
      if (managerIds.length) params.set('managerId', managerIds.join(','));
      if (groupIds.length) params.set('groupId', groupIds.join(','));
      if (boardIds.length) params.set('boardId', boardIds.join(','));
      if (sources.length) params.set('source', sources.join(','));

      const res = await fetch(`${API_BASE}/api/deal-time?${params}`, {
        headers: { Authorization: pb.authStore.token ? `Bearer ${pb.authStore.token}` : '' },
      });
      setData(await res.json() as DealTimeResponse);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [realizationStart, realizationEnd, creationStart, creationEnd, managerIds, groupIds, boardIds, sources]);

  useEffect(() => { void load(); }, [load]);

  const managerOptions = useMemo(
    () => users.filter(u => u.role !== 'ADMIN').map(u => ({ value: u.id, label: u.name || u.email })),
    [users],
  );
  const groupOptions = useMemo(
    () => groups.map(g => ({ value: g.id, label: g.name || `Группа ${g.id.slice(-4)}` })),
    [groups],
  );
  const stageOptions = useMemo(
    () => stages.map(s => ({ value: s.id, label: s.name })),
    [stages],
  );

  const activeCount =
    (realizationStart ? 1 : 0) + (realizationEnd ? 1 : 0) +
    (creationStart ? 1 : 0) + (creationEnd ? 1 : 0) +
    managerIds.length + groupIds.length + boardIds.length + sources.length;

  const reset = () => {
    setRealizationStart('');
    setRealizationEnd('');
    setCreationStart('');
    setCreationEnd('');
    setManagerIds([]);
    setGroupIds([]);
    setBoardIds([]);
    setSources([]);
  };

  return (
    <div className="adm-section">
      <div className="adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Среднее время сделки</h2>
          {data?.total != null && (
            <p className="adm-section-sub">
              {formatAvgDays(data.total.avgDays)} · {data.total.count} сделок
            </p>
          )}
        </div>
        <div className="dt-header-actions">
          <button
            type="button"
            className={`dt-filter-btn${filtersOpen ? ' active' : ''}${activeCount > 0 ? ' has-active' : ''}`}
            onClick={() => setFiltersOpen(v => !v)}
          >
            <IconFilter size={15} stroke={2} />
            Фильтры
            {activeCount > 0 && <span className="dt-filter-badge">{activeCount}</span>}
          </button>
          <button type="button" className="dt-refresh-btn" onClick={() => { void load(); }}>
            <IconRefresh size={15} stroke={2} />
          </button>
        </div>
      </div>

      {filtersOpen && (
        <div className="adm-analytics-filters dt-filters">
          <div className="dt-date-group">
            <span className="dt-date-label">Реализация</span>
            <DateInput className="adm-date-input" value={realizationStart} onChange={e => setRealizationStart(e.target.value)} />
            <span className="adm-date-sep">—</span>
            <DateInput className="adm-date-input" value={realizationEnd} onChange={e => setRealizationEnd(e.target.value)} />
          </div>
          <div className="dt-date-group">
            <span className="dt-date-label">Создание</span>
            <DateInput className="adm-date-input" value={creationStart} onChange={e => setCreationStart(e.target.value)} />
            <span className="adm-date-sep">—</span>
            <DateInput className="adm-date-input" value={creationEnd} onChange={e => setCreationEnd(e.target.value)} />
          </div>
          <div className="adm-analytics-employee">
            <Select mode="multi" options={managerOptions} value={managerIds} onChange={setManagerIds} placeholder="Все менеджеры" searchable />
          </div>
          <div className="adm-analytics-employee">
            <Select mode="multi" options={groupOptions} value={groupIds} onChange={setGroupIds} placeholder="Все группы" searchable />
          </div>
          <div className="adm-analytics-stages">
            <Select mode="multi" options={stageOptions} value={boardIds} onChange={setBoardIds} placeholder="Все этапы" />
          </div>
          <div className="adm-analytics-source">
            <Select mode="multi" fetchOptions={fetchSourceOptions} value={sources} onChange={setSources} placeholder="Все источники" searchable />
          </div>
          {activeCount > 0 && (
            <Button variant="adm-cancel" type="button" onClick={reset}>
              <IconX size={14} stroke={2} />Сбросить
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : !data?.managers ? (
        <div className="adm-empty">Нет данных</div>
      ) : data.managers.length === 0 && data.groups.length === 0 ? (
        <div className="adm-empty">Нет данных</div>
      ) : (
        <div className="af-blocks">
          {data.managers.length > 0 && <RankTable title="По менеджерам" rows={data.managers} />}
          {(data.groups ?? []).length > 0 && <RankTable title="По группам" rows={data.groups} />}
        </div>
      )}
    </div>
  );
};

export { DealTimeSection };
